/**
 * FT-267: Full-screen view for logging ad-hoc maintenance work that is not
 * tied to a scheduled job. Writes a `completionType: 'adhoc'` record into
 * maintenance_history via the parent's onSave handler.
 *
 * Refactored from LogMaintenanceModal (centered modal) to match AddJobView's
 * full-screen shell — sticky header, max-w-4xl container, card-based form
 * sections, inline action bar.
 *
 * All field behaviour, validation, audit integration, and save logic are
 * unchanged from the modal version.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  ClipboardCheck,
  ClipboardList,
  AlertTriangle,
  AlertCircle,
  FolderOpen,
  Calendar,
  Package,
  FileText,
  User,
  Clock,
  Loader2,
} from 'lucide-react';
import Dropdown from '../../../common/Dropdown';
import { CATEGORY_DEFINITIONS } from '../../../../config/maintenanceRequirements-wrapper';
import { getEquipmentDisplayName } from '../../../../services/equipment/utils/equipmentRow';

const OTHER_PERFORMER_VALUE = '__other__';
// FT-267 §16: same sentinel pattern reused for the category dropdown to trigger
// the conditional free-text "Custom category" input below.
const OTHER_CATEGORY_VALUE = '__other__';

const today = () => new Date().toISOString().split('T')[0];

const LogMaintenanceView = ({
  onBack,
  onSave,
  equipment = [],
  crew = [],
  currentUser = null,
  saveError = null,
}) => {
  // Pre-compute today's date once on mount so the `max` attribute matches the
  // validation guard below.
  const todayDate = useMemo(() => today(), []);

  // Default the performer dropdown to the current user's matching crew entry
  // (by id or name); otherwise leave blank so the user must pick.
  const defaultPerformer = useMemo(() => {
    if (!currentUser) return '';
    const byId = crew.find(c => c.id === currentUser.id || c.id === currentUser.crewId);
    if (byId) return byId.id;
    const byName = crew.find(c => (c.full_name || c.name) === currentUser.name);
    if (byName) return byName.id;
    return '';
  }, [crew, currentUser]);

  const [form, setForm] = useState({
    adhocItem: '',
    category: '',
    customCategory: '',
    date: todayDate,
    equipmentIds: [],
    notes: '',
    performedBy: defaultPerformer,
    externalPerformerName: '',
    hoursSpent: '',
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Re-seed performer once when crew/currentUser resolve a default.
  useEffect(() => {
    setForm(prev => (prev.performedBy ? prev : { ...prev, performedBy: defaultPerformer }));
  }, [defaultPerformer]);

  // FT-267 §16: adopt the unified canonical-code option list. The `__other__`
  // sentinel mirrors the performer "Other (specify)…" pattern and triggers the
  // conditional free-text input below.
  const categoryOptions = useMemo(() => {
    return [
      ...CATEGORY_DEFINITIONS,
      { value: OTHER_CATEGORY_VALUE, label: 'Other (specify)…' },
    ];
  }, []);

  const performerOptions = useMemo(() => {
    const crewOptions = crew.map(c => ({
      value: c.id,
      label: c.full_name || c.name || 'Unnamed',
    }));
    return [
      ...crewOptions,
      { value: OTHER_PERFORMER_VALUE, label: 'Other (specify)…' },
    ];
  }, [crew]);

  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const toggleEquipment = (id) => {
    setForm(prev => ({
      ...prev,
      equipmentIds: prev.equipmentIds.includes(id)
        ? prev.equipmentIds.filter(x => x !== id)
        : [...prev.equipmentIds, id],
    }));
  };

  const isOtherSelected = form.performedBy === OTHER_PERFORMER_VALUE;
  // FT-267 §16: mirrors performer "Other" — toggles the free-text input.
  const isOtherCategorySelected = form.category === OTHER_CATEGORY_VALUE;

  const validate = () => {
    const next = {};
    const trimmedItem = form.adhocItem.trim();
    if (trimmedItem.length < 3) {
      next.adhocItem = 'Please describe what was done (at least 3 characters)';
    }
    if (!form.category) {
      next.category = 'Please select a category';
    } else if (isOtherCategorySelected && form.customCategory.trim().length < 2) {
      // FT-267 §16: free-text custom category requires ≥2 chars after trim.
      next.customCategory = 'Please enter a custom category';
    }
    if (!form.date || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      next.date = 'Date is required';
    } else if (form.date > todayDate) {
      next.date = 'Date cannot be in the future';
    }
    if (!form.performedBy) {
      next.performedBy = 'Please select who performed the work';
    } else if (isOtherSelected && form.externalPerformerName.trim().length < 3) {
      next.externalPerformerName = 'Please enter performer name';
    }
    if (form.hoursSpent !== '' && form.hoursSpent !== null) {
      const n = Number(form.hoursSpent);
      if (!Number.isFinite(n) || n < 0) {
        next.hoursSpent = 'Hours must be a positive number';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!validate()) return;

    let performedById = null;
    let performerName = '';
    if (isOtherSelected) {
      performedById = null;
      performerName = form.externalPerformerName.trim();
    } else {
      performedById = form.performedBy;
      const selected = crew.find(c => c.id === form.performedBy);
      performerName = selected ? (selected.full_name || selected.name || '') : '';
    }

    const hoursValue = form.hoursSpent === '' ? null : Number(form.hoursSpent);

    // FT-267 §16: resolve the canonical category before save. When the user
    // picked "Other (specify)…", store the trimmed/lowercased free-text — the
    // raw `'__other__'` sentinel must never reach the record.
    const resolvedCategory = isOtherCategorySelected
      ? form.customCategory.trim().toLowerCase()
      : form.category;

    const payload = {
      adhocItem: form.adhocItem.trim(),
      category: resolvedCategory,
      date: form.date,
      equipmentIds: form.equipmentIds,
      notes: form.notes.trim(),
      performedBy: performedById,
      performerName,
      hoursSpent: hoursValue,
    };

    setIsSaving(true);
    try {
      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact header — matches AddJobView */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onBack}
                disabled={isSaving}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                Log Maintenance
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Form content — standardized container width to match AddJobView. */}
      {/* pb-24 md:pb-6 prevents the action bar from being hidden behind mobile bottom nav. */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">

            {saveError && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-red-800">{saveError}</span>
              </div>
            )}

            {/* What was done */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <ClipboardList className="h-5 w-5 mr-2 text-white" />
                  What was done
                </h3>
              </div>
              <div className="px-6 py-5">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="adhocItem" className="block text-sm font-medium text-gray-700 mb-1">
                      <ClipboardCheck className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="adhocItem"
                      rows={2}
                      value={form.adhocItem}
                      onChange={(e) => setField('adhocItem', e.target.value)}
                      placeholder="e.g., Replaced fuel filter on main engine"
                      className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                        errors.adhocItem ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                      } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                    />
                    {errors.adhocItem && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.adhocItem}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FolderOpen className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                        Category <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        value={form.category}
                        onChange={(val) => setField('category', val)}
                        options={categoryOptions}
                        placeholder="Select a category"
                        width="w-full"
                        error={!!errors.category}
                      />
                      {errors.category && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.category}
                        </p>
                      )}
                      {/* FT-267 §16: conditional free-text input when "Other
                          (specify)…" is selected. Mirrors the performer
                          "Other" pattern above. */}
                      {isOtherCategorySelected && (
                        <div className="mt-3">
                          <label htmlFor="customCategory" className="block text-sm font-medium text-gray-700 mb-1">
                            Custom category <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="customCategory"
                            type="text"
                            value={form.customCategory}
                            onChange={(e) => setField('customCategory', e.target.value)}
                            placeholder="e.g., Hydraulics"
                            aria-label="Custom category"
                            className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                              errors.customCategory ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                            } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                          />
                          {errors.customCategory && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {errors.customCategory}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <label htmlFor="adhocDate" className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                        Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="adhocDate"
                        type="date"
                        value={form.date}
                        max={todayDate}
                        onChange={(e) => setField('date', e.target.value)}
                        className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                          errors.date ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                        } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                      />
                      {errors.date && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.date}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Linked equipment (multi-select via checklist) */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <Package className="h-5 w-5 mr-2 text-white" />
                  Linked equipment <span className="ml-2 text-xs font-normal text-white/70">(optional)</span>
                </h3>
              </div>
              <div className="px-6 py-5">
                {equipment.length === 0 ? (
                  <p className="text-sm text-gray-500">No equipment available for this vessel.</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-3">
                      Select equipment items that this work applies to.
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-3">
                      {equipment.map(eq => {
                        const checked = form.equipmentIds.includes(eq.id);
                        const label = getEquipmentDisplayName(eq) || eq.id;
                        return (
                          <label
                            key={eq.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              checked
                                ? 'bg-cyan-50 border border-cyan-200'
                                : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleEquipment(eq.id)}
                              className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                            />
                            <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Performed by + Hours */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <User className="h-5 w-5 mr-2 text-white" />
                  Performed by
                </h3>
              </div>
              <div className="px-6 py-5">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                      Performed by <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                      value={form.performedBy}
                      onChange={(val) => setField('performedBy', val)}
                      options={performerOptions}
                      placeholder="Select who performed the work"
                      width="w-full"
                      error={!!errors.performedBy}
                    />
                    {errors.performedBy && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.performedBy}
                      </p>
                    )}
                    {isOtherSelected && (
                      <div className="mt-3">
                        <label htmlFor="externalPerformerName" className="block text-sm font-medium text-gray-700 mb-1">
                          External performer name <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="externalPerformerName"
                          type="text"
                          value={form.externalPerformerName}
                          onChange={(e) => setField('externalPerformerName', e.target.value)}
                          placeholder="e.g., Belfast Marine Services"
                          className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                            errors.externalPerformerName ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                          } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                        />
                        {errors.externalPerformerName && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {errors.externalPerformerName}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="hoursSpent" className="block text-sm font-medium text-gray-700 mb-1">
                      <Clock className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                      Hours spent <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id="hoursSpent"
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.hoursSpent}
                      onChange={(e) => setField('hoursSpent', e.target.value)}
                      placeholder="e.g., 0.5"
                      className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                        errors.hoursSpent ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                      } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                    />
                    <p className="mt-1 text-xs text-gray-500">Optional. Total hours for this work.</p>
                    {errors.hoursSpent && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.hoursSpent}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-white" />
                  Additional Notes
                </h3>
              </div>
              <div className="px-6 py-5">
                <label htmlFor="adhocNotes" className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="adhocNotes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Anything worth recording for next time"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>
            </div>

            {/* Form actions — in card to match AddJobView */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-6 py-5">
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onBack}
                  disabled={isSaving}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-3 md:py-2 text-base md:text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-h-[44px]"
                >
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSaving ? 'Logging…' : 'Log Maintenance'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogMaintenanceView;
